/**
 * Enhanced select component with built-in validation
 */

import React, { useEffect } from 'react'
import type { FieldValidationConfig } from '../types'
import { useFieldValidation } from '../hooks/useFieldValidation'

interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface ValidatedSelectProps {
  name: string
  label?: string
  value: string
  onChange: (name: string, value: string) => void
  onValidation?: (name: string, isValid: boolean, message?: string) => void
  validationConfig?: FieldValidationConfig
  options: SelectOption[]
  placeholder?: string
  helpText?: string
  required?: boolean
  disabled?: boolean
  className?: string
}

export const ValidatedSelect: React.FC<ValidatedSelectProps> = ({
  name,
  label,
  value,
  onChange,
  onValidation,
  validationConfig = {},
  options,
  placeholder,
  helpText,
  required,
  disabled,
  className = ''
}) => {
  const { validationState, validateField } = useFieldValidation({
    required,
    ...validationConfig
  })

  // Validate required fields on mount and when they become empty
  useEffect(() => {
    if (required) {
      const result = validateField(value)
      onValidation?.(name, result.isValid, result.message)
    }
  }, [required, name, validateField, onValidation, value])

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    onChange(name, newValue)
    
    // Validate on change if configured or if field was already validated
    if (validationConfig.validateOnChange || validationState.isValidated) {
      const result = validateField(newValue)
      onValidation?.(name, result.isValid, result.message)
    }
  }

  const handleBlur = () => {
    // Always validate on blur (default behavior)
    if (validationConfig.validateOnBlur !== false) {
      const result = validateField(value)
      onValidation?.(name, result.isValid, result.message)
    }
  }

  const getSelectClassName = () => {
    let selectClass = `validated-select ${className}`
    
    if (validationState.isValidated) {
      selectClass += validationState.isValid ? ' valid' : ' invalid'
    }
    
    return selectClass
  }

  const showValidationIcon = validationConfig.showSuccessIcon !== false && validationState.isValidated

  return (
    <div className="validated-select-wrapper">
      {label && (
        <label htmlFor={name} className="validated-select-label">
          {label}
          {required && <span className="required-indicator"> *</span>}
        </label>
      )}
      
      <div className="validated-select-container">
        <select
          id={name}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          className={getSelectClassName()}
          aria-invalid={validationState.isValidated && !validationState.isValid}
          aria-describedby={
            (validationState.message && `${name}-error`) || 
            (helpText && `${name}-help`) || 
            undefined
          }
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        
        {showValidationIcon && (
          <div className="validation-icon">
            {validationState.isValid ? (
              <i className="bi bi-check-circle text-success" aria-hidden="true"></i>
            ) : (
              <i className="bi bi-exclamation-circle text-danger" aria-hidden="true"></i>
            )}
          </div>
        )}
      </div>
      
      {validationState.isValidated && validationState.message && (
        <div 
          id={`${name}-error`}
          className="validation-message error"
          role="alert"
          aria-live="polite"
        >
          <i className="bi bi-exclamation-triangle me-1" aria-hidden="true"></i>
          {validationState.message}
        </div>
      )}
      
      {helpText && !validationState.message && (
        <div id={`${name}-help`} className="help-text">
          {helpText}
        </div>
      )}
    </div>
  )
}
